import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Box, TextField, Stack, Typography, InputAdornment, Chip, CircularProgress, IconButton, Button, ToggleButton, ToggleButtonGroup } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { MobileDateTimePicker, MobileDatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import Properties from './Properties';
import MetadataSection from './MetadataSection';
import Pipeline from './Pipeline';
import PipelineStages from './PipelineStages';
import VoiceInput from './VoiceInput';
import CompanyAutocomplete from './CompanyAutocomplete';
import ContactAutocomplete from './ContactAutocomplete';
import api from '../../Services/Api';
import { filterAllowlistedProperties, isAllowlistedProperty, shouldKeepCustomProperty } from '../propertyAllowlist';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ShortTextIcon from '@mui/icons-material/ShortText';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { glassCard, ACCENT, ACCENT_DARK, TEXT_SECONDARY, TEXT_TERTIARY, TEXT_PRIMARY, noMotion } from '../../designsystem/tokens';

const EMAIL_FORMAT_REGEX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const EMAIL_QUICK_ACTIONS = [
    { key: 'makeShorter', icon: <ShortTextIcon sx={{ fontSize: 18 }} />, instruction: 'Make this email more concise while keeping all key information' },
    { key: 'makeProfessional', icon: <BusinessCenterIcon sx={{ fontSize: 18 }} />, instruction: 'Rewrite in a professional business tone' },
    { key: 'addUrgency', icon: <PriorityHighIcon sx={{ fontSize: 18 }} />, instruction: 'Add a sense of urgency to this email' },
    { key: 'makeFriendly', icon: <SentimentSatisfiedIcon sx={{ fontSize: 18 }} />, instruction: 'Rewrite in a warm, friendly tone' },
];

/**
 * CRMObjectForm — generic form that renders ANY CRMObject.
 *
 * Layout:
 *   1. Core Properties (group: 'main') — dedicated fields
 *   2. Metadata Section — pipeline/direction/calendar (entity-specific)
 *   3. Details (group: 'details') — secondary core props
 *   4. Custom Properties — admin-configured via Properties.js
 *
 * No association section — associations are 100% backend-managed.
 */
const CRMObjectForm = ({ crmObject, onChange, setNextDisabled, loaded, showGoogleMeet, lockedMode = false }) => {
    const { t } = useTranslation();
    const [stageProperties, setStageProperties] = useState(null);
    const [stagePropertiesLoading, setStagePropertiesLoading] = useState(false);
    const [stagePropertiesError, setStagePropertiesError] = useState(null);

    const objectType = crmObject.objectType;
    const properties = crmObject.properties || [];
    const effectiveProperties = filterAllowlistedProperties(properties);
    const metadata = crmObject.metadata || {};

    const isEditMode = crmObject.operation === 'update' && Boolean(crmObject.id);
    // Contacts have a 3-way toggle (edit / create / select). Companies still
    // use the binary create/select toggle and hide it in edit mode.
    const isContact = objectType === 'contact';
    const hasToggle = isContact || (objectType === 'company' && !isEditMode);
    // Derive contact 3-way mode. The user's explicit choice in metadata.mode
    // wins — without it, "Seleccionar" without picking a contact would show as
    // 'create' (id is null) and let the user advance with create=false + no id,
    // which the backend rejects.
    const explicitMode = crmObject.metadata?.mode;
    const contactMode = isContact
        ? (explicitMode === 'select' ? 'select'
            : explicitMode === 'edit' ? 'edit'
            : explicitMode === 'create' ? 'create'
            : crmObject.operation === 'update' && crmObject.id ? 'edit'
            : crmObject.id ? 'select' : 'create')
        : null;
    // Stash the originally-resolved id so the user can flip create→edit back
    // without losing the AI's match. Survives across toggles.
    const stashedEditId = crmObject.metadata?._stashedEditId || (contactMode === 'edit' ? crmObject.id : null);
    // For companies (legacy binary toggle): edit-mode renders create-like.
    // For contacts: edit and create modes both render the editable form,
    // select renders the autocomplete.
    const isCreateMode = isContact
        ? (contactMode === 'edit' || contactMode === 'create')
        : (isEditMode ? true : metadata.create !== false);

    // Pre-load company/contact options when starting in "select existing" mode
    useEffect(() => {
        if (!hasToggle || isCreateMode) return;
        if (crmObject.options?.length > 0) return;
        if (objectType === 'company') {
            api.fetchCompanies({ value: '' }).then((result) => {
                if (result?.options?.length > 0) {
                    onChange({ ...crmObject, options: result.options });
                }
            }).catch(() => {});
        }
        if (objectType === 'contact') {
            api.fetchContacts({ firstName: '' }).then((result) => {
                if (result?.options?.length > 0) {
                    onChange({ ...crmObject, options: result.options });
                }
            }).catch(() => {});
        }
    }, [objectType, isCreateMode]);

    // Group properties — exclude metadata-managed props from form fields
    const DEAL_METADATA_PROPS = new Set(['dealstage', 'pipeline']);
    const CALL_METADATA_PROPS = new Set(['direction']);
    const MEETING_METADATA_PROPS = new Set(['startDate', 'endTime']);
    const EMAIL_METADATA_PROPS = new Set(['sender']);
    const isMetadataProp = (p) =>
        (objectType === 'deal' && DEAL_METADATA_PROPS.has(p.name)) ||
        (objectType === 'call' && CALL_METADATA_PROPS.has(p.name)) ||
        (objectType === 'meeting' && MEETING_METADATA_PROPS.has(p.name)) ||
        (objectType === 'email' && EMAIL_METADATA_PROPS.has(p.name));

    const mainProps = effectiveProperties.filter(p => p.core && p.group === 'main' && !isMetadataProp(p));
    const detailProps = effectiveProperties.filter(p => p.core && p.group === 'details');
    const internalProps = effectiveProperties.filter(p => p.core && p.group === 'internal');
    const customProps = effectiveProperties.filter(
        p => !p.core && shouldKeepCustomProperty(p) && !(objectType === 'deal' && DEAL_METADATA_PROPS.has(p.name))
    );

    // Fetch stage-specific properties when deal stage changes
    useEffect(() => {
        if (objectType === 'deal' && metadata.pipeline?.id && metadata.stage?.id) {
            setStagePropertiesLoading(true);
            setStagePropertiesError(null);
            api.fetchProperties('deal', true, false, metadata.pipeline.id, metadata.stage.id)
                .then(props => setStageProperties(props || []))
                .catch((err) => {
                    setStageProperties([]);
                    setStagePropertiesError(err || new Error('stage-properties-fetch-failed'));
                })
                .finally(() => setStagePropertiesLoading(false));
        }
    }, [objectType, metadata.pipeline?.id, metadata.stage?.id]);

    // Sync metadata selections → CRM properties (hidden from UI)
    useEffect(() => {
        const updates = {};
        if (objectType === 'deal') {
            if (metadata.stage?.id) {
                const stageProp = properties.find(p => p.name === 'dealstage');
                if (stageProp && stageProp.value !== metadata.stage.id) updates.dealstage = metadata.stage.id;
            }
            if (metadata.pipeline?.id) {
                const pipelineProp = properties.find(p => p.name === 'pipeline');
                if (pipelineProp && pipelineProp.value !== metadata.pipeline.id) updates.pipeline = metadata.pipeline.id;
            }
        }
        if (objectType === 'call' && metadata.direction) {
            const dirProp = properties.find(p => p.name === 'direction');
            if (dirProp && dirProp.value !== metadata.direction) updates.direction = metadata.direction;
        }
        if (objectType === 'meeting') {
            if (metadata.computedStartDate) {
                const startProp = properties.find(p => p.name === 'startDate');
                if (startProp && startProp.value !== metadata.computedStartDate) updates.startDate = metadata.computedStartDate;
            }
            if (metadata.computedEndTime) {
                const endProp = properties.find(p => p.name === 'endTime');
                if (endProp && endProp.value !== metadata.computedEndTime) updates.endTime = metadata.computedEndTime;
            }
        }
        if (Object.keys(updates).length > 0) {
            const updated = {
                ...crmObject,
                properties: crmObject.properties.map(p =>
                    updates[p.name] !== undefined ? { ...p, value: updates[p.name] } : p
                ),
            };
            onChange(updated);
        }
    }, [objectType, metadata.stage?.id, metadata.pipeline?.id, metadata.direction, metadata.computedStartDate, metadata.computedEndTime]);

    useEffect(() => {
        if (loaded) loaded();
    }, [loaded]);

    // Validate: check mandatory fields
    useEffect(() => {
        if (!setNextDisabled) return;

        // In "select existing" mode, only need a selected entity
        if (hasToggle && !isCreateMode) {
            setNextDisabled(!crmObject.id);
            return;
        }

        const isValid = effectiveProperties.every(p => {
            // Skip metadata-managed props (dealstage via stage chips, direction via MetadataSection)
            if (isMetadataProp(p)) return true;
            let isMandatory = p.mandatory === true;
            if (p.mandatoryIf) {
                const dep = effectiveProperties.find(d => d.name === p.mandatoryIf);
                if (dep && dep.value != null && dep.value !== '') isMandatory = true;
            }
            if (p.type === 'number' && p.value !== '' && p.value !== undefined && p.value !== null && Number.isNaN(Number(p.value))) {
                return false;
            }
            if (!isMandatory) return true;
            if (typeof p.value === 'string') return p.value.trim() !== '';
            return p.value !== undefined && p.value !== null && p.value !== '';
        });

        // Additional metadata validation
        let metadataValid = true;
        if (objectType === 'deal') {
            metadataValid = Boolean(metadata.pipeline?.id && metadata.stage?.id);
        }
        if (objectType === 'email') {
            // Block submission unless every meaningful recipient has a syntactically
            // valid email. A "meaningful" recipient is one with a name, email, or id —
            // empty placeholder slots ({}) are ignored.
            const recipients = Array.isArray(metadata.recipient) ? metadata.recipient : [];
            const meaningful = recipients.filter(r => r && (r.firstname || r.lastname || r.email || r.id));
            const hasOne = meaningful.length > 0;
            const allHaveValidEmail = meaningful.every(r => typeof r.email === 'string' && EMAIL_FORMAT_REGEX.test(r.email.trim()));
            metadataValid = hasOne && allHaveValidEmail;
        }

        // Validate stage-specific mandatory properties
        let stageValid = true;
        if (stageProperties && stageProperties.length > 0) {
            const existingNames = new Set(properties.map(p => p.name));
            const STAGE_EXCLUDE = new Set([...DEAL_METADATA_PROPS, 'amount', 'dealname', 'closedate']);
            const filteredStage = stageProperties.filter((p) =>
                isAllowlistedProperty(p) && shouldKeepCustomProperty(p) && !existingNames.has(p.name) && !STAGE_EXCLUDE.has(p.name)
            );
            stageValid = filteredStage.every(p => {
                if (!p.mandatory) return true;
                if (typeof p.value === 'string') return p.value.trim() !== '';
                return p.value !== undefined && p.value !== null && p.value !== '';
            });
        }

        const stageFetchBlocking = objectType === 'deal' && metadata.pipeline?.id && metadata.stage?.id && (stagePropertiesLoading || Boolean(stagePropertiesError));
        setNextDisabled(!isValid || !metadataValid || !stageValid || stageFetchBlocking);
    }, [effectiveProperties, properties, metadata, objectType, setNextDisabled, stageProperties, stagePropertiesLoading, stagePropertiesError]);

    const updateProperty = useCallback((name, value) => {
        const updated = {
            ...crmObject,
            properties: crmObject.properties.map(p =>
                p.name === name ? { ...p, value, error: undefined, _emailNeedsReview: undefined } : p
            ),
        };
        // Update displayName based on primary field
        updated.displayName = buildDisplayName(objectType, updated.properties);
        onChange(updated);
    }, [crmObject, objectType, onChange]);

    const updateMetadata = useCallback((newMetadata) => {
        onChange({ ...crmObject, metadata: newMetadata });
    }, [crmObject, onChange]);

    const updateCustomProperties = useCallback((customPropArray) => {
        const coreProps = crmObject.properties.filter(p => p.core);
        // Split back: regular custom props go into crmObject, stage props stay in local state
        const regularCustom = customPropArray.filter(p => !p._isStage);
        const stageCustom = customPropArray.filter(p => p._isStage);
        onChange({ ...crmObject, properties: [...coreProps, ...regularCustom.map(p => ({ ...p, core: false }))] });
        if (stageCustom.length > 0) setStageProperties(stageCustom.map(({ _isStage, ...p }) => p));
    }, [crmObject, onChange]);

    // ─── Email-specific state & handlers ─────────────────────────────
    const [emailLoadingAction, setEmailLoadingAction] = useState(null);
    const [showCcBcc, setShowCcBcc] = useState(false);
    const [recentAttachments, setRecentAttachments] = useState([]);
    const [loadingRecentAttachments, setLoadingRecentAttachments] = useState(false);
    const [uploadingAttachments, setUploadingAttachments] = useState(false);
    const [attachmentError, setAttachmentError] = useState('');
    const attachmentInputRef = useRef(null);

    const attachmentIdOf = useCallback((attachment) => {
        if (!attachment || typeof attachment !== 'object') return null;
        return attachment.attachmentId || attachment.id || null;
    }, []);

    const mergeUniqueAttachments = useCallback((attachments) => {
        const map = new Map();
        (attachments || []).forEach((attachment) => {
            const id = attachmentIdOf(attachment);
            if (!id) return;
            if (!map.has(id)) map.set(id, attachment);
        });
        return Array.from(map.values());
    }, [attachmentIdOf]);

    const selectedAttachments = useMemo(() => mergeUniqueAttachments(metadata.attachments || []), [metadata.attachments, mergeUniqueAttachments]);

    useEffect(() => {
        if (objectType !== 'email') return undefined;
        let active = true;
        setLoadingRecentAttachments(true);
        api.fetchRecentEmailAttachments(12)
            .then((items) => {
                if (!active) return;
                setRecentAttachments(Array.isArray(items) ? items : []);
            })
            .catch(() => {
                if (!active) return;
                setRecentAttachments([]);
            })
            .finally(() => {
                if (active) setLoadingRecentAttachments(false);
            });

        return () => {
            active = false;
        };
    }, [objectType]);

    const removeAttachment = useCallback((attachmentId) => {
        const updated = selectedAttachments.filter((item) => attachmentIdOf(item) !== attachmentId);
        updateMetadata({ ...metadata, attachments: updated });
    }, [attachmentIdOf, metadata, selectedAttachments, updateMetadata]);

    const toggleRecentAttachment = useCallback((attachment) => {
        const attachmentId = attachmentIdOf(attachment);
        if (!attachmentId) return;
        const exists = selectedAttachments.some((item) => attachmentIdOf(item) === attachmentId);
        if (exists) {
            removeAttachment(attachmentId);
            return;
        }
        updateMetadata({
            ...metadata,
            attachments: mergeUniqueAttachments([...selectedAttachments, attachment]),
        });
    }, [attachmentIdOf, mergeUniqueAttachments, metadata, removeAttachment, selectedAttachments, updateMetadata]);

    const formatAttachmentSize = useCallback((sizeBytes) => {
        const size = Number(sizeBytes || 0);
        if (!size) return '';
        if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;
        return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    }, []);

    const onAttachmentInputChange = useCallback(async (event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = '';
        if (files.length === 0) return;

        setUploadingAttachments(true);
        setAttachmentError('');
        try {
            const uploaded = await api.uploadEmailAttachments(files);
            const normalizedUploaded = Array.isArray(uploaded) ? uploaded : [];
            updateMetadata({
                ...metadata,
                attachments: mergeUniqueAttachments([...selectedAttachments, ...normalizedUploaded]),
            });
            setRecentAttachments((prev) => mergeUniqueAttachments([...normalizedUploaded, ...prev]));
        } catch (error) {
            setAttachmentError(error?.data?.message || t('attachmentUploadFailed'));
        } finally {
            setUploadingAttachments(false);
        }
    }, [mergeUniqueAttachments, metadata, selectedAttachments, t, updateMetadata]);

    const handleEmailQuickAction = useCallback(async (action) => {
        if (emailLoadingAction) return;
        const messageProp = properties.find(p => p.name === 'message');
        if (!messageProp?.value?.trim()) return;
        setEmailLoadingAction(action.key);
        try {
            const result = await api.RewriteText(messageProp.value, action.instruction);
            updateProperty('message', result.text);
        } catch { /* silent */ }
        setEmailLoadingAction(null);
    }, [emailLoadingAction, properties, updateProperty]);

    /** Reusable recipient field renderer for To / CC / BCC */
    const renderRecipientField = (field, label) => {
        const recipients = (metadata[field] || []).filter(r => r && (r.id || r.email || r.firstname || r.lastname));
        const hasEmpty = (metadata[field] || []).some(r => !r || (!r.id && !r.email && !r.firstname && !r.lastname));
        const opts = (crmObject.options || []).filter(o => o && o.email);

        return (
            <Box sx={{ mt: field === 'recipient' ? 0 : 1.5 }}>
                <Typography variant="caption" sx={{ color: TEXT_TERTIARY, fontWeight: 600, display: 'block', textTransform: 'uppercase', fontSize: '0.68rem', mb: 0.5 }}>
                    {label}
                </Typography>
                {recipients.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                        {recipients.map((r, i) => (
                            <Chip
                                key={r.id || r.email || i}
                                label={`${r.firstname || ''} ${r.lastname || ''}`.trim() + (r.email ? ` (${r.email})` : '')}
                                onDelete={() => {
                                    const updated = recipients.filter((_, idx) => idx !== i);
                                    updateMetadata({ ...metadata, [field]: updated });
                                }}
                                data-testid={`${field}-chip-${i}`}
                                sx={{
                                    height: 44,
                                    backgroundColor: 'rgba(118,169,172,0.15)',
                                    border: '1px solid rgba(118,169,172,0.3)',
                                    color: TEXT_PRIMARY,
                                    '& .MuiChip-deleteIcon': { fontSize: 20, color: TEXT_SECONDARY },
                                    ...noMotion,
                                }}
                            />
                        ))}
                    </Box>
                )}
                {hasEmpty ? (
                    <ContactAutocomplete
                        opts={opts}
                        contact={{}}
                        setContact={(contact) => {
                            if (contact?.id || contact?.email || contact?.firstname) {
                                updateMetadata({ ...metadata, [field]: [...recipients, contact] });
                            }
                        }}
                    />
                ) : (
                    <IconButton
                        onClick={() => updateMetadata({ ...metadata, [field]: [...(metadata[field] || []), {}] })}
                        sx={{ minWidth: 48, minHeight: 48, color: ACCENT }}
                        data-testid={`add-${field}`}
                        aria-label={`${t('addRecipient')} ${label}`}
                    >
                        <AddCircleIcon />
                    </IconButton>
                )}
            </Box>
        );
    };

    // Humanize raw enum labels: "NOT_STARTED" -> "Not Started"
    const humanizeLabel = useCallback((label) => {
        if (!label) return '';
        const translated = t(label);
        if (translated !== label) return translated;
        // Fallback: convert UPPER_SNAKE_CASE or camelCase to Title Case
        return label
            .replace(/_/g, ' ')
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .toLowerCase()
            .replace(/\b\w/g, c => c.toUpperCase());
    }, [t]);

    // Detect HTML input type from property name for mobile-optimized keyboards
    const getInputTypeFromName = (name) => {
        const n = (name || '').toLowerCase();
        if (n.includes('email') || n.includes('correo')) return 'email';
        if (n.includes('phone') || n.includes('telefono') || n.includes('mobile') || n.includes('fax')) return 'tel';
        if (n.includes('domain') || n.includes('website') || n.includes('url')) return 'url';
        return 'text';
    };

    const renderField = (prop) => {
        const isTextarea = prop.fieldType === 'textarea';
        const isNumber = prop.type === 'number';
        const isDate = prop.type === 'date' || prop.type === 'datetime';
        const isEnum = prop.type === 'enumeration';

        if (isEnum && prop.options?.length > 0) {
            return (
                <Box key={prop.name} sx={{ width: '100%' }}>
                    <Typography variant="caption" sx={{ color: TEXT_SECONDARY, fontWeight: 600, mb: 0.5, display: 'block' }}>
                        {t(prop.labelKey) || prop.name}
                        {prop.mandatory && <span style={{ color: ACCENT }}> *</span>}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                        {prop.options.map(opt => (
                            <Box
                                key={opt.value}
                                data-testid={`${prop.name}-${opt.value}`}
                                onClick={() => updateProperty(prop.name, opt.value)}
                                sx={{
                                    px: 2, py: 1,
                                    borderRadius: '12px',
                                    border: `1px solid ${prop.value === opt.value ? ACCENT : 'rgba(255,255,255,0.2)'}`,
                                    backgroundColor: prop.value === opt.value ? `${ACCENT}15` : 'transparent',
                                    color: prop.value === opt.value ? ACCENT : TEXT_PRIMARY,
                                    cursor: 'pointer',
                                    minHeight: 44,
                                    display: 'flex',
                                    alignItems: 'center',
                                    fontSize: '0.85rem',
                                    fontWeight: prop.value === opt.value ? 600 : 400,
                                    transition: 'all 200ms ease-out',
                                    ...noMotion,
                                }}
                            >
                                {humanizeLabel(opt.label)}
                            </Box>
                        ))}
                    </Stack>
                </Box>
            );
        }

        if (isDate) {
            return (
                <LocalizationProvider dateAdapter={AdapterDayjs} key={prop.name}>
                    {prop.type === 'date' ? (
                        <MobileDatePicker
                            label={t(prop.labelKey) || prop.name}
                            value={prop.value ? dayjs(prop.value) : null}
                            onChange={(val) => updateProperty(prop.name, val ? val.format('YYYY-MM-DD') : '')}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    required: prop.mandatory,
                                    error: Boolean(prop.error),
                                    helperText: prop.error ? t(prop.error) || prop.error : undefined,
                                    'data-testid': `field-${prop.name}`,
                                    'aria-label': t(prop.labelKey) || prop.name,
                                    slotProps: { inputLabel: { shrink: true } },
                                    sx: { '& .MuiOutlinedInput-root': { borderRadius: '12px', minHeight: 44 } },
                                },
                            }}
                        />
                    ) : (
                        <MobileDateTimePicker
                            label={t(prop.labelKey) || prop.name}
                            value={prop.value ? dayjs(prop.value) : null}
                            onChange={(val) => updateProperty(prop.name, val ? val.toISOString() : '')}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    required: prop.mandatory,
                                    error: Boolean(prop.error),
                                    helperText: prop.error ? t(prop.error) || prop.error : undefined,
                                    'data-testid': `field-${prop.name}`,
                                    'aria-label': t(prop.labelKey) || prop.name,
                                    slotProps: { inputLabel: { shrink: true } },
                                    sx: { '& .MuiOutlinedInput-root': { borderRadius: '12px', minHeight: 44 } },
                                },
                            }}
                        />
                    )}
                </LocalizationProvider>
            );
        }

        // Determine the correct input type
        let inputType = 'text';
        if (isNumber) inputType = 'number';
        else if (!isTextarea) inputType = getInputTypeFromName(prop.name);

        const isEmailField = prop.name === 'email' || prop.fieldType === 'email' || inputType === 'email';
        const rawEmailValue = isEmailField && typeof prop.value === 'string' ? prop.value.trim() : '';
        const emailFormatInvalid = isEmailField && rawEmailValue !== '' && !EMAIL_FORMAT_REGEX.test(rawEmailValue);
        const emailNeedsReview = isEmailField && Boolean(prop._emailNeedsReview);
        const showEmailError = emailFormatInvalid || emailNeedsReview;
        const fieldError = Boolean(prop.error) || showEmailError;
        let helperText;
        if (prop.error) helperText = t(prop.error) || prop.error;
        else if (showEmailError) helperText = t('emailInvalidHint', { value: prop.value || '' });

        return (
            <TextField
                key={prop.name}
                fullWidth
                label={t(prop.labelKey) || prop.name}
                value={prop.value || ''}
                onChange={(e) => {
                    if (isNumber) {
                        if (e.target.value === '') {
                            updateProperty(prop.name, '');
                            return;
                        }
                        const parsed = Number(e.target.value);
                        updateProperty(prop.name, Number.isNaN(parsed) ? '' : parsed);
                        return;
                    }
                    updateProperty(prop.name, e.target.value);
                }}
                required={prop.mandatory}
                error={fieldError}
                helperText={helperText}
                multiline={isTextarea}
                minRows={isTextarea ? 3 : undefined}
                type={inputType}
                data-testid={`field-${prop.name}`}
                aria-label={t(prop.labelKey) || prop.name}
                slotProps={{
                    input: {
                        endAdornment: isTextarea ? (
                            <InputAdornment position="end">
                                <VoiceInput value={prop.value || ''} onResult={(text) => updateProperty(prop.name, text)} />
                            </InputAdornment>
                        ) : undefined,
                    },
                    inputLabel: { shrink: true },
                }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        minHeight: 44,
                    },
                }}
            />
        );
    };

    const toggleCreateMode = useCallback(() => {
        const newCreate = !isCreateMode;
        const updated = {
            ...crmObject,
            metadata: { ...crmObject.metadata, create: newCreate },
            // Reset selection when switching to create, reset id when switching to select
            ...(newCreate ? { id: null, operation: 'create' } : {}),
        };
        onChange(updated);
    }, [crmObject, isCreateMode, onChange]);

    const setContactMode = useCallback((nextMode) => {
        if (!nextMode || nextMode === contactMode) return;
        // Always stash the current resolved id (if any) so flips don't lose it.
        const stashId = crmObject.id || crmObject.metadata?._stashedEditId || null;
        let updated;
        if (nextMode === 'edit') {
            updated = {
                ...crmObject,
                id: crmObject.id || stashId,
                operation: 'update',
                metadata: { ...crmObject.metadata, create: false, mode: 'edit', _stashedEditId: stashId },
            };
        } else if (nextMode === 'select') {
            updated = {
                ...crmObject,
                operation: 'create',
                metadata: { ...crmObject.metadata, create: false, mode: 'select', _stashedEditId: stashId },
            };
        } else {
            // create — drop id so we don't accidentally update an existing record
            updated = {
                ...crmObject,
                id: null,
                operation: 'create',
                metadata: { ...crmObject.metadata, create: true, mode: 'create', _stashedEditId: stashId },
            };
        }
        onChange(updated);
    }, [crmObject, contactMode, onChange]);

    const handleAutocompleteSelect = useCallback((selected) => {
        if (!selected) return;
        const updated = {
            ...crmObject,
            id: selected.id,
            operation: 'update',
            displayName: objectType === 'company'
                ? (selected.name || '')
                : `${selected.firstname || ''} ${selected.lastname || ''}`.trim(),
        };
        // Also update property values from the selected entity
        if (objectType === 'company') {
            updated.properties = crmObject.properties.map(p => {
                if (p.name === 'name' && selected.name) return { ...p, value: selected.name };
                if (p.name === 'domain' && selected.domain) return { ...p, value: selected.domain };
                return p;
            });
        } else if (objectType === 'contact') {
            updated.properties = crmObject.properties.map(p => {
                if (p.name === 'firstname' && selected.firstname) return { ...p, value: selected.firstname };
                if (p.name === 'lastname' && selected.lastname) return { ...p, value: selected.lastname };
                if (p.name === 'email' && selected.email) return { ...p, value: selected.email };
                return p;
            });
        }
        onChange(updated);
    }, [crmObject, objectType, onChange]);

    return (
        <Stack spacing={2.5} sx={{ width: '100%' }}>
            {/* Edit-mode banner for company only (contact uses 3-way toggle below) */}
            {isEditMode && objectType === 'company' && (
                <Stack direction="row" alignItems="center" spacing={1}
                    sx={{ ...glassCard, py: 1.5, px: 2, border: `1px solid ${ACCENT}40` }}>
                    <Typography variant="body2" sx={{ color: ACCENT, fontWeight: 600 }}>
                        {t('editing')} · {t(objectType)}
                        {crmObject.displayName ? ` · ${crmObject.displayName}` : ''}
                    </Typography>
                </Stack>
            )}

            {/* Contact: locked banner when entry is edit-contact (no toggle) */}
            {isContact && lockedMode && (
                <Box sx={{ ...glassCard, py: 1.5, px: 2, border: `1px solid ${ACCENT}40` }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <EditIcon sx={{ fontSize: 18, color: ACCENT }} />
                        <Typography variant="body2" sx={{ color: ACCENT, fontWeight: 600 }}>
                            {t('editing')} · {t('contact')}
                            {crmObject.displayName ? ` · ${crmObject.displayName}` : ''}
                        </Typography>
                    </Stack>
                    <Typography variant="caption" sx={{ color: TEXT_TERTIARY, lineHeight: 1.4, display: 'block', mt: 0.5 }}>
                        {t('contactModeEditHelp', { name: crmObject.displayName || t('contact') })}
                    </Typography>
                </Box>
            )}

            {/* Contact: 3-way segmented control (edit / create / select) */}
            {isContact && !lockedMode && (
                <Box sx={{ ...glassCard, py: 1.5, px: 2 }}>
                    <Stack spacing={1.25}>
                        <ToggleButtonGroup
                            value={contactMode}
                            exclusive
                            onChange={(_e, next) => setContactMode(next)}
                            fullWidth
                            data-testid="contact-mode-toggle"
                            sx={{
                                '& .MuiToggleButton-root': {
                                    minHeight: 44,
                                    color: TEXT_SECONDARY,
                                    border: '1px solid rgba(255,255,255,0.12)',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    gap: 0.75,
                                    transition: 'all 200ms ease-out',
                                },
                                '& .MuiToggleButton-root.Mui-selected': {
                                    backgroundColor: `${ACCENT}25`,
                                    color: ACCENT,
                                    borderColor: ACCENT,
                                },
                                '& .MuiToggleButton-root.Mui-selected:hover': {
                                    backgroundColor: `${ACCENT}35`,
                                },
                                ...noMotion,
                            }}
                        >
                            <ToggleButton value="edit" data-testid="contact-mode-edit" disabled={!crmObject.id && !stashedEditId}>
                                <EditIcon sx={{ fontSize: 18 }} />
                                {t('contactModeEdit')}
                            </ToggleButton>
                            <ToggleButton value="create" data-testid="contact-mode-create">
                                <AddIcon sx={{ fontSize: 18 }} />
                                {t('contactModeCreate')}
                            </ToggleButton>
                            <ToggleButton value="select" data-testid="contact-mode-select">
                                <SearchIcon sx={{ fontSize: 18 }} />
                                {t('contactModeSelect')}
                            </ToggleButton>
                        </ToggleButtonGroup>
                        <Typography variant="caption" sx={{ color: TEXT_TERTIARY, lineHeight: 1.4 }}>
                            {t(
                                contactMode === 'edit' ? 'contactModeEditHelp'
                                    : contactMode === 'select' ? 'contactModeSelectHelp'
                                    : 'contactModeCreateHelp',
                                { name: crmObject.displayName || t('contact') }
                            )}
                        </Typography>
                    </Stack>
                </Box>
            )}

            {/* Company: legacy binary toggle (hidden in edit mode) */}
            {!isContact && hasToggle && (
                <Stack direction="row" alignItems="center" justifyContent="space-between"
                    sx={{ ...glassCard, py: 1.5, px: 2 }}>
                    <Typography variant="body2" sx={{ color: TEXT_PRIMARY, fontWeight: 500 }}>
                        {isCreateMode ? t('newCompany') : t('existingCompany')}
                    </Typography>
                    <Chip
                        label={isCreateMode ? t('selectExisting') : t('createNew')}
                        onClick={toggleCreateMode}
                        data-testid={`toggle-create-${objectType}`}
                        sx={{
                            backgroundColor: `${ACCENT}15`,
                            color: ACCENT,
                            fontWeight: 600,
                            minHeight: 36,
                            borderRadius: '10px',
                            '&:hover': { backgroundColor: `${ACCENT}25` },
                            ...noMotion,
                        }}
                    />
                </Stack>
            )}

            {/* Autocomplete (select existing mode) */}
            {hasToggle && !isCreateMode && (
                <Box sx={{ width: '100%' }}>
                    {objectType === 'company' ? (
                        <CompanyAutocomplete
                            opts={crmObject.options || []}
                            company={{ id: crmObject.id, name: crmObject.displayName }}
                            setCompany={handleAutocompleteSelect}
                        />
                    ) : (
                        <ContactAutocomplete
                            opts={crmObject.options || []}
                            contact={{
                                id: crmObject.id,
                                firstname: properties.find(p => p.name === 'firstname')?.value || '',
                                lastname: properties.find(p => p.name === 'lastname')?.value || '',
                                email: properties.find(p => p.name === 'email')?.value || '',
                            }}
                            setContact={handleAutocompleteSelect}
                        />
                    )}
                </Box>
            )}

            {/* Email composer */}
            {objectType === 'email' && (
                <Stack spacing={2}>
                    {/* Recipients card: To + CC/BCC */}
                    <Box sx={{ ...glassCard }}>
                        {renderRecipientField('recipient', t('recipients'))}

                        <Typography
                            variant="caption"
                            onClick={() => setShowCcBcc(!showCcBcc)}
                            data-testid="toggle-cc-bcc"
                            sx={{
                                color: ACCENT,
                                cursor: 'pointer',
                                fontWeight: 600,
                                display: 'inline-block',
                                mt: 1.5,
                                fontSize: '0.75rem',
                                '&:hover': { textDecoration: 'underline' },
                                ...noMotion,
                            }}
                        >
                            CC / BCC {showCcBcc ? '▴' : '▾'}
                        </Typography>

                        {showCcBcc && (
                            <>
                                {renderRecipientField('cc', 'CC')}
                                {renderRecipientField('bcc', 'BCC')}
                            </>
                        )}
                    </Box>

                    {/* Email body card: Subject + Quick Actions + Message */}
                    <Box sx={{ ...glassCard }}>
                        <Stack spacing={2}>
                            {mainProps.filter(p => p.name === 'subject').map(renderField)}

                            <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.06)', pt: 1.5 }}>
                                <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1.5 }}>
                                    {EMAIL_QUICK_ACTIONS.map((action) => (
                                        <Chip
                                            key={action.key}
                                            icon={emailLoadingAction === action.key ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : action.icon}
                                            label={t(action.key)}
                                            data-testid={`email-action-${action.key}`}
                                            disabled={!properties.find(p => p.name === 'message')?.value?.trim() || Boolean(emailLoadingAction && emailLoadingAction !== action.key)}
                                            onClick={() => handleEmailQuickAction(action)}
                                            sx={{
                                                height: 44,
                                                borderRadius: '22px',
                                                flexShrink: 0,
                                                backgroundColor: emailLoadingAction === action.key ? ACCENT_DARK : 'rgba(255,255,255,0.08)',
                                                color: emailLoadingAction === action.key ? '#fff' : 'rgba(255,255,255,0.9)',
                                                border: emailLoadingAction === action.key ? `2px solid ${ACCENT_DARK}` : '1px solid rgba(255,255,255,0.15)',
                                                '& .MuiChip-icon': {
                                                    color: emailLoadingAction === action.key ? '#fff' : TEXT_SECONDARY,
                                                },
                                                ...noMotion,
                                            }}
                                        />
                                    ))}
                                </Box>
                                {mainProps.filter(p => p.name !== 'subject').map(renderField)}
                            </Box>
                        </Stack>
                    </Box>

                    {/* TODO: Attachments card — temporarily hidden
                    <Box sx={{ ...glassCard }}>
                        <Typography variant="caption" sx={{ color: TEXT_TERTIARY, fontWeight: 600, display: 'block', textTransform: 'uppercase', fontSize: '0.68rem', mb: 0.5 }}>
                            {t('attachments')}
                        </Typography>

                        {selectedAttachments.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                                {selectedAttachments.map((attachment, idx) => {
                                    const attachmentId = attachmentIdOf(attachment) || `${idx}`;
                                    const attachmentLabel = `${attachment.name || t('attachments')} ${formatAttachmentSize(attachment.sizeBytes) ? `(${formatAttachmentSize(attachment.sizeBytes)})` : ''}`.trim();
                                    return (
                                        <Chip
                                            key={attachmentId}
                                            icon={<AttachFileIcon sx={{ fontSize: 16 }} />}
                                            label={attachmentLabel}
                                            onDelete={() => removeAttachment(attachmentId)}
                                            data-testid={`attachment-chip-${attachmentId}`}
                                            sx={{
                                                height: 44,
                                                maxWidth: '100%',
                                                backgroundColor: 'rgba(118,169,172,0.15)',
                                                border: '1px solid rgba(118,169,172,0.3)',
                                                color: TEXT_PRIMARY,
                                                '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                                                '& .MuiChip-deleteIcon': { fontSize: 20, color: TEXT_SECONDARY },
                                                ...noMotion,
                                            }}
                                        />
                                    );
                                })}
                            </Box>
                        )}

                        <Button
                            variant="outlined"
                            startIcon={uploadingAttachments ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <UploadFileIcon />}
                            data-testid="upload-email-attachment"
                            aria-label={t('uploadAttachment')}
                            onClick={() => attachmentInputRef.current?.click()}
                            disabled={uploadingAttachments}
                            sx={{
                                minHeight: 44,
                                borderRadius: '12px',
                                textTransform: 'none',
                                borderColor: 'rgba(118,169,172,0.45)',
                                color: TEXT_PRIMARY,
                                mb: 1,
                            }}
                        >
                            {uploadingAttachments ? t('uploadingAttachment') : t('uploadAttachment')}
                        </Button>

                        <input
                            ref={attachmentInputRef}
                            type="file"
                            multiple
                            hidden
                            onChange={onAttachmentInputChange}
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.png,.jpg,.jpeg,.webp,.gif"
                        />

                        <Typography variant="caption" sx={{ color: TEXT_TERTIARY, display: 'block', mb: 0.5 }}>
                            {t('recentAttachments')}
                        </Typography>

                        {loadingRecentAttachments ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                                <CircularProgress size={18} />
                            </Box>
                        ) : recentAttachments.length > 0 ? (
                            <Box className="hide-scrollbar" sx={{ display: 'flex', gap: 0.75, overflowX: 'auto', pb: 0.5 }}>
                                {recentAttachments.map((attachment) => {
                                    const attachmentId = attachmentIdOf(attachment);
                                    const isSelected = selectedAttachments.some((item) => attachmentIdOf(item) === attachmentId);
                                    return (
                                        <Chip
                                            key={attachmentId}
                                            icon={<AttachFileIcon sx={{ fontSize: 16 }} />}
                                            label={attachment.name}
                                            clickable
                                            onClick={() => toggleRecentAttachment(attachment)}
                                            data-testid={`recent-attachment-${attachmentId}`}
                                            sx={{
                                                height: 44,
                                                borderRadius: '12px',
                                                flexShrink: 0,
                                                border: `1px solid ${isSelected ? ACCENT : 'rgba(255,255,255,0.2)'}`,
                                                backgroundColor: isSelected ? `${ACCENT}20` : 'rgba(255,255,255,0.06)',
                                                color: isSelected ? ACCENT : TEXT_PRIMARY,
                                                '& .MuiChip-label': { maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' },
                                                ...noMotion,
                                            }}
                                        />
                                    );
                                })}
                            </Box>
                        ) : (
                            <Typography variant="caption" sx={{ color: TEXT_SECONDARY }}>
                                {t('noRecentAttachments')}
                            </Typography>
                        )}

                        {attachmentError && (
                            <Typography variant="caption" sx={{ color: '#ef5350', display: 'block', mt: 0.75 }}>
                                {attachmentError}
                            </Typography>
                        )}
                    </Box>
                    */}
                </Stack>
            )}

            {/* Core Properties: main group (hidden in select existing mode) — non-email */}
            {objectType !== 'email' && (!hasToggle || isCreateMode) && objectType !== 'deal' && mainProps.length > 0 && (
                <Stack spacing={2}>
                    {mainProps.map(renderField)}
                </Stack>
            )}

            {/* Deal: main fields + pipeline + stages in a single "negocio" card */}
            {objectType === 'deal' && (!hasToggle || isCreateMode) && (
                <Box sx={{ ...glassCard }}>
                    <Stack spacing={2.5}>
                        {mainProps.length > 0 && (
                            <Stack spacing={2}>
                                {mainProps.map(renderField)}
                            </Stack>
                        )}
                        <Pipeline
                            dealPipeline={metadata.pipeline || undefined}
                            selectPipeline={(pipeline) => {
                                const updates = { ...metadata, pipeline };
                                if (pipeline?.stages?.length === 1) {
                                    updates.stage = pipeline.stages[0];
                                } else if (metadata.pipeline?.id !== pipeline?.id) {
                                    updates.stage = null;
                                }
                                updateMetadata(updates);
                            }}
                            loaded={() => {}}
                            setNextDisabled={() => {}}
                        />
                        {metadata.pipeline?.stages?.length > 0 && (
                            <PipelineStages
                                stagePipeline={metadata.stage || undefined}
                                selectStage={(stage) => updateMetadata({ ...metadata, stage })}
                                stages={metadata.pipeline.stages}
                                loaded={() => {}}
                                setNextDisabled={() => {}}
                            />
                        )}
                    </Stack>
                </Box>
            )}

            {/* Metadata Section (call direction / meeting calendar) — hidden in select mode */}
            {(!hasToggle || isCreateMode) && objectType !== 'deal' && (
                <MetadataSection
                    objectType={objectType}
                    metadata={metadata}
                    onChange={updateMetadata}
                    showGoogleMeet={showGoogleMeet}
                    properties={properties}
                />
            )}

            {objectType === 'deal' && stagePropertiesLoading && (
                <Box sx={{ ...glassCard, py: 1.5, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress size={20} />
                </Box>
            )}

            {objectType === 'deal' && stagePropertiesError && (
                <Box sx={{ ...glassCard, border: '1px solid rgba(239,68,68,0.35)', py: 1.5 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                        <Typography variant="body2" sx={{ color: TEXT_SECONDARY }}>
                            {t('errorProcessingRequestDescription')}
                        </Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                                setStagePropertiesError(null);
                                setStagePropertiesLoading(true);
                                api.fetchProperties('deal', true, false, metadata.pipeline?.id, metadata.stage?.id)
                                    .then(props => setStageProperties(props || []))
                                    .catch((err) => {
                                        setStageProperties([]);
                                        setStagePropertiesError(err || new Error('stage-properties-fetch-failed'));
                                    })
                                    .finally(() => setStagePropertiesLoading(false));
                            }}
                            sx={{ minHeight: 36 }}
                        >
                            {t('retry')}
                        </Button>
                    </Stack>
                </Box>
            )}

            {/* Core Properties: details group */}
            {(!hasToggle || isCreateMode) && detailProps.length > 0 && (
                <Box>
                    <Typography variant="caption" sx={{ color: TEXT_TERTIARY, fontWeight: 600, mb: 1, display: 'block', textTransform: 'uppercase', fontSize: '0.68rem' }}>
                        {t('details')}
                    </Typography>
                    <Stack spacing={2}>
                        {detailProps.map(renderField)}
                    </Stack>
                </Box>
            )}

            {/* Core Properties: internal group */}
            {(!hasToggle || isCreateMode) && internalProps.length > 0 && (
                <Box>
                    <Typography variant="caption" sx={{ color: TEXT_TERTIARY, fontWeight: 600, mb: 1, display: 'block', textTransform: 'uppercase', fontSize: '0.68rem' }}>
                        {t('internalNotes')}
                    </Typography>
                    <Stack spacing={2}>
                        {internalProps.map(renderField)}
                    </Stack>
                </Box>
            )}

            {/* Unified Properties section: custom + filtered stage properties merged */}
            {(() => {
                const merged = [...customProps];
                if (objectType === 'deal' && stageProperties) {
                    const existingNames = new Set(properties.map(p => p.name));
                    // Also exclude metadata props (dealstage managed by stage chips, amount/dealname by core fields)
                    const STAGE_EXCLUDE = new Set([...DEAL_METADATA_PROPS, 'amount', 'dealname', 'closedate']);
                    const filtered = stageProperties.filter((p) =>
                        isAllowlistedProperty(p) &&
                        shouldKeepCustomProperty(p) &&
                        !existingNames.has(p.name) &&
                        !STAGE_EXCLUDE.has(p.name)
                    );
                    merged.push(...filtered.map(p => ({ ...p, _isStage: true })));
                }
                return merged.length > 0 ? (
                    <Properties
                        properties={merged}
                        setProperties={updateCustomProperties}
                    />
                ) : null;
            })()}
        </Stack>
    );
};

function buildDisplayName(objectType, properties) {
    const get = (name) => {
        const p = properties.find(pr => pr.name === name);
        return p?.value || '';
    };
    switch (objectType) {
        case 'deal': return get('dealname');
        case 'contact': return `${get('firstname')} ${get('lastname')}`.trim();
        case 'company': return get('name');
        case 'task': return get('subject');
        case 'meeting': return get('title');
        case 'note': return (get('note') || '').substring(0, 50);
        case 'email': return get('subject');
        case 'call': return get('title');
        default: return '';
    }
}

export default CRMObjectForm;
